import { useEffect } from "react";
import type { Term } from "../dataTypes";

interface TermModalProps {
  term: Term;
  relatedTerms: Term[];
  onPick: (term: Term) => void;
  onClose: () => void;
}

export default function TermModal({ term, relatedTerms, onPick, onClose }: TermModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground-950/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-background-50 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="shrink-0 border-b border-background-200 px-6 py-4 md:px-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-800">
                {term.tag}
              </span>
              <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
                {term.category}
              </span>
            </div>
            <button
              aria-label="关闭"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900 cursor-pointer"
            >
              <i className="ri-close-line text-lg leading-none"></i>
            </button>
          </div>
          <h3 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {term.title}
          </h3>
          <p className="mt-2 text-sm leading-6 text-foreground-500">{term.summary}</p>
        </div>

        {/* 内容滚动区 */}
        <div className="overflow-y-auto px-6 py-5 md:px-8" id="termModalBody">
          {/* 释义 */}
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-primary-700">
              <i className="ri-file-list-3-line text-base leading-none"></i>
              详细释义
            </div>
            <div className="mt-3 space-y-3">
              {term.content.map((para, i) => (
                <p key={i} className="text-[15px] leading-7 text-foreground-700">
                  {para}
                </p>
              ))}
            </div>
          </div>

          {/* 溯源 */}
          <div className="mt-6 rounded-lg bg-primary-50/70 p-4 ring-1 ring-primary-100">
            <div className="flex items-center gap-2 text-sm font-semibold text-primary-800">
              <i className="ri-history-line text-base leading-none"></i>
              溯源 · 怎么来的
            </div>
            <p className="mt-2 text-sm leading-6 text-foreground-700">{term.origin}</p>
          </div>

          {/* 用法 */}
          <div className="mt-4 rounded-lg bg-secondary-50 p-4 ring-1 ring-secondary-100">
            <div className="flex items-center gap-2 text-sm font-semibold text-secondary-900">
              <i className="ri-chat-smile-3-line text-base leading-none"></i>
              常见用法
            </div>
            <p className="mt-2 text-sm leading-6 text-foreground-700">{term.usage}</p>
          </div>

          {/* 提示 */}
          <div className="mt-4 rounded-lg bg-accent-50 p-4 ring-1 ring-accent-100">
            <div className="flex items-center gap-2 text-sm font-semibold text-accent-800">
              <i className="ri-lightbulb-flash-line text-base leading-none"></i>
              温馨提示 · 理性吃瓜
            </div>
            <p className="mt-2 text-sm leading-6 text-foreground-700">{term.tips}</p>
          </div>

          {/* 相关词条 */}
          {relatedTerms.length > 0 && (
            <div className="mt-6 border-t border-background-200 pt-4">
              <div className="text-xs font-medium text-foreground-500">相关词条</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {relatedTerms.map((rt) => (
                  <button
                    key={rt.id}
                    onClick={() => onPick(rt)}
                    className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-sm font-medium text-primary-700 ring-1 ring-background-200 transition-colors hover:bg-primary-100 hover:ring-primary-300 cursor-pointer"
                  >
                    {rt.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}